import {
  PERMISSION_ACTION_COMPLETE,
  PERMISSION_ACTION_DELETE,
  PERMISSION_ACTION_EDIT,
  PERMISSION_ACTION_PUBLISH,
  PERMISSION_ENTITY_PAYMENT
} from '@klear/constants'
import { PAYABLE_ACTION_MARK_PAID } from '@klear/payable/constants'
import type { Payable } from '@klear/payable/payable'
import {
  PAYMENT_FEE_ENTITY_TYPES,
  PAYMENT_TYPE_PAAS,
  PAYMENT_TYPE_PARTICIPATORY_FEES,
  PAYMENT_TYPE_SUPPLIER
} from '@klear/payment/constants'
import { paymentIsFailed } from '@klear/payment/status'

export type PayableLifecycleAction =
  | typeof PERMISSION_ACTION_PUBLISH
  | typeof PERMISSION_ACTION_EDIT
  | typeof PERMISSION_ACTION_DELETE
  | typeof PERMISSION_ACTION_COMPLETE
  | typeof PAYABLE_ACTION_MARK_PAID

export const PAYABLE_ACTION_LABELS: Record<PayableLifecycleAction, string> = {
  [PERMISSION_ACTION_EDIT]: 'Edit',
  [PERMISSION_ACTION_PUBLISH]: 'Schedule',
  [PERMISSION_ACTION_COMPLETE]: 'Send Now',
  [PAYABLE_ACTION_MARK_PAID]: 'Mark as Paid',
  [PERMISSION_ACTION_DELETE]: 'Delete'
}

const RETRY_LABEL = 'Retry'

export const PAY_NOW_SENDING_TOAST = {
  title: 'Sending payment',
  description: "We'll email you as soon as Stripe accepts it, or if it is rejected."
}

const FEE_PAYMENT_TYPES = new Set<string>([...PAYMENT_FEE_ENTITY_TYPES, PAYMENT_TYPE_PARTICIPATORY_FEES])

export type PayableListBucket = 'draft' | 'scheduled' | 'sent'

export type PayablePermissionChecker = {
  hasPermission(entity: string, action: string): boolean
}

export type PayableActionOption = {
  label: string
  value: PayableLifecycleAction
}

export const PAYABLE_UNSAVED_ID_DISPLAY = 'Payment'

export function payableIdDisplay(idDisplay?: string | null): string {
  return idDisplay != null && idDisplay.length > 0 ? idDisplay : PAYABLE_UNSAVED_ID_DISPLAY
}

const PAYABLE_NO_ORDER_LABEL = 'No order'

export function payableTypeLabel(row: Pick<Payable, 'orderId' | 'orderIdDisplay' | 'paymentType'>): string {
  if (row.orderId != null && row.orderIdDisplay) return row.orderIdDisplay
  return row.paymentType === PAYMENT_TYPE_SUPPLIER ? PAYABLE_NO_ORDER_LABEL : row.paymentType
}

export function payableIsEarlypay(row: Pick<Payable, 'paymentType'>): boolean {
  return row.paymentType === PAYMENT_TYPE_PAAS
}

export function payableIsFee(row: Pick<Payable, 'paymentType'>): boolean {
  return FEE_PAYMENT_TYPES.has(row.paymentType)
}

export function payableIsOutbound(row: Pick<Payable, 'sourceAccountOrganizationId' | 'traderOrganizationId'>): boolean {
  return (
    row.traderOrganizationId != null &&
    row.traderOrganizationId > 0 &&
    row.sourceAccountOrganizationId === row.traderOrganizationId
  )
}

export function payableAvailableActions(
  row: Payable,
  user: PayablePermissionChecker,
  allowedActions: readonly PayableLifecycleAction[]
): PayableActionOption[] {
  const options: PayableActionOption[] = []

  if (
    allowedActions.includes(PERMISSION_ACTION_EDIT) &&
    row.canEdit &&
    user.hasPermission(PERMISSION_ENTITY_PAYMENT, PERMISSION_ACTION_EDIT)
  ) {
    options.push({ label: PAYABLE_ACTION_LABELS[PERMISSION_ACTION_EDIT], value: PERMISSION_ACTION_EDIT })
  }
  if (
    allowedActions.includes(PERMISSION_ACTION_PUBLISH) &&
    row.canPublish &&
    user.hasPermission(PERMISSION_ENTITY_PAYMENT, PERMISSION_ACTION_PUBLISH)
  ) {
    options.push({ label: PAYABLE_ACTION_LABELS[PERMISSION_ACTION_PUBLISH], value: PERMISSION_ACTION_PUBLISH })
  }
  if (
    allowedActions.includes(PERMISSION_ACTION_COMPLETE) &&
    row.canComplete &&
    user.hasPermission(PERMISSION_ENTITY_PAYMENT, PERMISSION_ACTION_COMPLETE)
  ) {
    options.push({
      label: paymentIsFailed(row.effectiveStatus) ? RETRY_LABEL : PAYABLE_ACTION_LABELS[PERMISSION_ACTION_COMPLETE],
      value: PERMISSION_ACTION_COMPLETE
    })
  }
  if (
    allowedActions.includes(PAYABLE_ACTION_MARK_PAID) &&
    row.canMarkPaid &&
    user.hasPermission(PERMISSION_ENTITY_PAYMENT, PERMISSION_ACTION_COMPLETE)
  ) {
    options.push({ label: PAYABLE_ACTION_LABELS[PAYABLE_ACTION_MARK_PAID], value: PAYABLE_ACTION_MARK_PAID })
  }
  if (
    allowedActions.includes(PERMISSION_ACTION_DELETE) &&
    row.canDelete &&
    user.hasPermission(PERMISSION_ENTITY_PAYMENT, PERMISSION_ACTION_DELETE)
  ) {
    options.push({ label: PAYABLE_ACTION_LABELS[PERMISSION_ACTION_DELETE], value: PERMISSION_ACTION_DELETE })
  }

  return options
}

export const PAYABLE_ROW_ACTIONS: PayableLifecycleAction[] = [
  PERMISSION_ACTION_EDIT,
  PERMISSION_ACTION_PUBLISH,
  PERMISSION_ACTION_COMPLETE,
  PAYABLE_ACTION_MARK_PAID,
  PERMISSION_ACTION_DELETE
]

export const SCHEDULED_PAYABLE_ACTIONS: PayableLifecycleAction[] = [
  PERMISSION_ACTION_EDIT,
  PERMISSION_ACTION_COMPLETE,
  PAYABLE_ACTION_MARK_PAID,
  PERMISSION_ACTION_DELETE
]
